import { SoggettoDto } from "@api/client";
import { subjectUpdateSubject } from "common/_redux/subject/actions";
import AppDatePicker from "common/components/app-date-picker";
import BftBooleanButton from "common/components/boolean-button";
import BftButton from "common/components/button";
import BftInfoTooltip from "common/components/info-tooltip";
import BftInput from "common/components/input";
import MessageModal from "common/components/message-modal";
import BftRadioGroup from "common/components/radio-button";
import BftSelect from "common/components/select";
import { Formik, carichePubblicheSchema } from "common/validators/validators";
import { BooleanValues, StatiCarichiPendenti, TipoSoggetto } from "constants/global";
import { condanneOptions } from "constants/options";
import { useAppDispatch } from "core/store/hooks";
import { useEffect, useState } from "react";
import { Col, Form, Row } from "react-bootstrap";

const { TRUE, FALSE } = BooleanValues;

interface Props {
	subjectDetails?: SoggettoDto;
	idPratica?: string | undefined;
	idCliente?: string | undefined;
	onComplete: () => void;
	skipSaveStepStatus?: boolean;
}

const CarichePubbliche: React.FC<Props> = (props: Props) => {
	const { subjectDetails, idPratica, idCliente, onComplete, skipSaveStepStatus = false } = props;

	const dispatch = useAppDispatch();

	const [showModal, setShowModal] = useState<boolean>(false);
	const [showModalEstero, setShowModalEstero] = useState<boolean>(false);
	const [showModalResidenza, setShowModalResidenza] = useState<boolean>(false);

	const [nazioneResidenza, setNazioneResidenza] = useState<string>(
		subjectDetails?.nazioneResidenza || ""
	);

	useEffect(() => {
		setNazioneResidenza(subjectDetails?.nazioneResidenza || "IT");
	}, [subjectDetails]);

	const _handleSubmit = (values: SoggettoDto) => {
		let subject: SoggettoDto = {
			...subjectDetails,
			...values,
		};

		dispatch(subjectUpdateSubject({ subject, skipSaveStepStatus }))
			.unwrap()
			.then((res) => {
				if (res) {
					onComplete();
				}
			});
	};

	return (
		<>
			<Formik
				initialValues={{
					hasCarichePubbliche: subjectDetails?.hasCarichePubbliche || false,
					caricaRicoperta: subjectDetails?.caricaRicoperta || "",
					fatca: subjectDetails?.fatca || false,
					hasCarichiPendenti: subjectDetails?.hasCarichiPendenti,
					autoritaEmanante: subjectDetails?.autoritaEmanante || "",
					oggettoProvvedimento: subjectDetails?.oggettoProvvedimento || "",
					dataEmissione: subjectDetails?.dataEmissione || undefined,
					statoProvvedimento: subjectDetails?.statoProvvedimento || "",
				}}
				onSubmit={_handleSubmit}
				validationSchema={carichePubblicheSchema}
				enableReinitialize
			>
				{({
					handleSubmit,
					handleChange,
					setFieldValue,
					submitForm,
					validateForm,
					values,
					touched,
					errors,
				}) => {
					const handleChangeCustom = (input: string, type?: string) => (e: any) => {
						if (type === "date") {
							setFieldValue(input, e);
							return;
						}
						const value = typeof e === "object" ? e.target?.value : e;
						handleChange(value);
						setFieldValue(input, value === TRUE);
					};
					return (
						<Form noValidate onSubmit={handleSubmit}>
							<div className="d-flex align-items-center" style={{ height: "1rem" }}>
								<div>
									<h6 className="container my-3">
										Sei una persona politicamente esposta o hai altre cariche?
									</h6>
								</div>
							</div>

							<BftBooleanButton
								label="Per maggiori dettagli su persona politicamente esposta o su quali sono le altre cariche"
								name="hasCarichePubbliche"
								value={FALSE}
								onChange={handleChangeCustom("hasCarichePubbliche")}
								className="mb-0"
							/>
							<a
								className="container text-primary fw-semibold text-decoration-none popup"
								onClick={() => setShowModal(true)}
								style={{ cursor: "pointer" }}
							>
								LEGGI DI PIÙ
							</a>

							{errors.hasCarichePubbliche && touched.hasCarichePubbliche && (
								<Form.Control.Feedback className="ms-3 mt-0" type="invalid">
									{errors.hasCarichePubbliche}
								</Form.Control.Feedback>
							)}
							{values.hasCarichePubbliche && (
								<Row>
									<Col md={9}>
										<BftInput
											required
											type={"text"}
											name={"caricaRicoperta"}
											placeholder={"Carica ricoperta*"}
											className="mt-3"
											value={values.caricaRicoperta?.toUpperCase()}
											onChange={handleChange}
											isInvalid={!!errors.caricaRicoperta && touched.caricaRicoperta}
										/>
										{errors.caricaRicoperta && touched.caricaRicoperta && (
											<Form.Control.Feedback className="ms-3 mt-0" type="invalid">
												{errors.caricaRicoperta}
											</Form.Control.Feedback>
										)}
									</Col>
								</Row>
							)}

							<div className="d-flex align-items-center mt-4" style={{ height: "1rem" }}>
								<div>
									<h6 className="container my-3">Residenza Fiscale</h6>
								</div>
							</div>
							<BftBooleanButton
								label="Sei residente o cittadino USA o hai una green card (US Person)?"
								name="fatca"
								value={FALSE}
								onChange={handleChangeCustom("fatca")}
								className="mb-0"
							/>
							<a
								className="container text-primary fw-semibold text-decoration-none popup"
								onClick={() => setShowModalEstero(true)}
								style={{ cursor: "pointer" }}
							>
								LEGGI DI PIÙ
							</a>

							{errors.fatca && touched.fatca && (
								<Form.Control.Feedback className="ms-3 mt-0" type="invalid">
									{errors.fatca}
								</Form.Control.Feedback>
							)}

							<BftBooleanButton
								label="Sei fiscalmente residente all'estero?"
								name="crs"
								onChange={() => {}}
								value={nazioneResidenza !== "IT" ? TRUE : FALSE}
								className="mb-0"
							/>

							<a
								className="container text-primary fw-semibold text-decoration-none popup"
								onClick={() => setShowModalResidenza(true)}
								style={{ cursor: "pointer" }}
							>
								LEGGI DI PIÙ
							</a>

							<p className="container mt-4 mb-2">
								Continuando dichiaro di non avere altre residenze fiscali e mi impegno a comunicare
								tempestivamente all'Istituto eventuali variazioni. Le informazioni che ti abbiamo
								richiesto sono necessarie ai sensi delle normative Fatca e Crs e dalla L. n.95/2015
								per verificare se sei una US Person o se sei fiscalmente residente in un altro paese
								estero.
							</p>

							{subjectDetails?.tipoSoggetto === TipoSoggetto.LR && (
								<>
									<div className="d-flex align-items-center">
										<div>
											<h6 className="container mt-4 mb-2">
												Dichiarazione liberatoria da carichi pendenti e casellario giudiziario
											</h6>
										</div>
										<BftInfoTooltip
											className="mt-4 mb-2 col-1"
											message="Dichiarazione rilasciata con la piena consapevolezza delle sanzioni penali, nel caso di dichiarazioni non veritiere, di formazione o uso di atti falsi richiamate dall’art. 76 del DPR 28 dicembre 2000, n. 445, e della decadenza dai benefici eventualmente conseguiti con provvedimenti emanati in base a dichiarazioni non veritiere, ai sensi dell’art. 75 del DPR 28 dicembre 2000, n. 445"
										/>
									</div>
									<p className="container">Spuntare uno dei due punti riportati in basso</p>
									<div>
										<Row>
											<Col style={{ textAlign: "justify", textJustify: "auto" }}>
												<BftRadioGroup
													controlId="hasCarichiPendenti"
													options={condanneOptions}
													onChange={handleChangeCustom("hasCarichiPendenti")}
													selectedOption={values.hasCarichiPendenti}
													isInvalid={!!errors.hasCarichiPendenti && touched.hasCarichiPendenti}
												/>
												{errors.hasCarichiPendenti && touched.hasCarichiPendenti && (
													<Form.Control.Feedback className="ms-3 mt-0" type="invalid">
														{errors.hasCarichiPendenti}
													</Form.Control.Feedback>
												)}
											</Col>
										</Row>
									</div>
								</>
							)}

							{values.hasCarichiPendenti && (
								<div className="mt-3">
									<Row>
										<Col md={5}>
											<BftInput
												required
												type={"text"}
												name={"autoritaEmanante"}
												placeholder={"Autorità emanante*"}
												value={values.autoritaEmanante?.toUpperCase()}
												onChange={handleChange}
												isInvalid={!!errors.autoritaEmanante && touched.autoritaEmanante}
											/>
											{errors.autoritaEmanante && touched.autoritaEmanante && (
												<Form.Control.Feedback className="ms-3 mt-0" type="invalid">
													{errors.autoritaEmanante}
												</Form.Control.Feedback>
											)}
										</Col>
										<Col md={7}>
											<BftInput
												required
												type={"text"}
												name={"oggettoProvvedimento"}
												placeholder={"Oggetto provvedimento*"}
												value={values.oggettoProvvedimento?.toUpperCase()}
												onChange={handleChange}
												isInvalid={!!errors.oggettoProvvedimento && touched.oggettoProvvedimento}
											/>
											{errors.oggettoProvvedimento && touched.oggettoProvvedimento && (
												<Form.Control.Feedback className="ms-3 mt-0" type="invalid">
													{errors.oggettoProvvedimento}
												</Form.Control.Feedback>
											)}
										</Col>
									</Row>
									<Row>
										<Col md={5}>
											<div className="mx-3 mb-3">
												<AppDatePicker
													label="Data emissione*"
													name="dataEmissione"
													value={values.dataEmissione}
													onChange={handleChangeCustom("dataEmissione", "date")}
												/>
												{errors.dataEmissione && touched.dataEmissione && (
													<Form.Control.Feedback className="ms-0 mt-3" type="invalid">
														{errors.dataEmissione}
													</Form.Control.Feedback>
												)}
											</div>
										</Col>
										<Col md={7}>
											<BftSelect
												name={"statoProvvedimento"}
												placeholder={"Stato*"}
												value={values.statoProvvedimento}
												onChange={handleChange}
												options={StatiCarichiPendenti}
												isInvalid={!!errors.statoProvvedimento && touched.statoProvvedimento}
											/>
											{errors.statoProvvedimento && touched.statoProvvedimento && (
												<Form.Control.Feedback className="ms-3 mt-0" type="invalid">
													{errors.statoProvvedimento}
												</Form.Control.Feedback>
											)}
										</Col>
									</Row>
								</div>
							)}

							<div className="mx-2 my-4">
								<BftButton className="btn-buffetti col-12" type="submit">
									Avanti
								</BftButton>
							</div>
						</Form>
					);
				}}
			</Formik>

			<MessageModal
				show={showModal}
				onClose={() => setShowModal(false)}
				title="D.lgs 231/2007 Art. 1, comma 2"
				message={getInfoCariche()}
				className="large-modal"
			/>

			<MessageModal
				show={showModalEstero}
				onClose={() => setShowModalEstero(false)}
				title="Fatca"
				message={getEstero()}
				className="large-modal"
			/>

			<MessageModal
				show={showModalResidenza}
				onClose={() => setShowModalResidenza(false)}
				title="Residenza Fiscale"
				message={getResidenza()}
				className="large-modal"
			/>
		</>
	);
};

const getInfoCariche = () => {
	return (
		<p style={{ whiteSpace: "pre-line" }}>
			D.lgs 231/2007 Art. 1, comma 2 dd) "persone politicamente esposte": le persone fisiche che
			occupano o hanno cessato di occupare da meno di un anno importanti cariche pubbliche, nonché i
			loro familiari e coloro che con i predetti soggetti intrattengono notoriamente stretti legami,
			come di seguito elencate: Sono persone fisiche che occupano o hanno occupato importanti
			cariche pubbliche coloro che ricoprono o hanno ricoperto la carica di: {"\n"}
			<span className="m-0">1.1</span> Presidente della Repubblica, Presidente del Consiglio,
			Ministro, Vice-Ministro e Sottosegretario, Presidente di Regione, assessore regionale, Sindaco
			di capoluogo di provincia o città metropolitana, Sindaco di comune con popolazione non
			inferiore a 15.000 abitanti nonché cariche analoghe in Stati esteri; {"\n"}
			<span className="m-0">1.2</span>
			&nbsp;Deputato, senatore, parlamentare europeo, consigliere regionale nonché cariche analoghe
			in Stati esteri;
			{"\n"}
			<span className="m-0">1.3</span> Membro degli organi direttivi centrali di partiti politici;{" "}
			{"\n"}
			<span className="m-0">1.4</span>
			&nbsp;Giudice della Corte Costituzionale, magistrato della Corte di Cassazione o della Corte
			dei conti, consigliere di Stato e altri componenti del Consiglio di Giustizia Amministrativa
			per la Regione siciliana nonché cariche analoghe in Stati esteri; {"\n"}
			<span className="m-0">1.5</span> Membro degli organi direttivi delle banche centrali e delle
			autorità indipendenti; {"\n"}
			<span className="m-0">1.6</span> Ambasciatore, incaricato d'affari ovvero cariche equivalenti
			in Stati esteri, ufficiale di grado apicale delle forze armate ovvero cariche analoghe in
			Stati esteri; {"\n"}
			<span className="m-0">1.7</span> Componente degli organi di amministrazione, direzione o
			controllo delle imprese controllate, anche indirettamente, dallo Stato italiano o da uno Stato
			estero ovvero partecipate, in misura prevalente o totalitaria, dalle Regioni, da comuni
			capoluoghi di provincia e città metropolitane e da comuni con popolazione complessivamente non
			inferiore a 15.000 abitanti; {"\n"}
			<span className="m-0">1.8</span> Direttore generale di ASL e di azienda ospedaliera, di
			azienda ospedaliera universitaria e degli altri enti del servizio sanitario nazionale. {"\n"}
			<span className="m-0">1.9</span>
			&nbsp;direttore, vicedirettore e membro dell'organo di gestione o soggetto svolgenti funzioni
			equivalenti in organizzazioni internazionali;{"\n"}
			Sono familiari di persone politicamente esposte: i genitori, il coniuge o la persona legata in
			unione civile o convivenza di fatto o istituti assimilabili alla persona politicamente
			esposta, i figli e i loro coniugi nonché le persone legate ai figli in unione civile o
			convivenza di fatto o istituti assimilabili;{"\n"}
			Sono soggetti con i quali le persone politicamente esposte intrattengono notoriamente stretti
			legami: {"\n"}
			<span className="m-0">3.1</span>
			&nbsp;le persone fisiche legate alla persona politicamente esposta per via della titolarità
			effettiva congiunta di enti giuridici o di altro stretto rapporto di affari;{"\n"}
			<span className="m-0">3.2</span>
			&nbsp;le persone fisiche che detengono solo formalmente il controllo totalitario di un'entità
			notoriamente costituita, di fatto, nell'interesse e a beneficio di una persona politicamente
			esposta.
			{"\n"}
			Nozione di Politico Italiano Locale (PIL) - Disposizioni in materia di adeguata verifica della
			clientela per il contrasto del riciclaggio e de l finanziamento del terrorismo (Banca
			d’Italia, 30 luglio 2019 e ss.mm.ii.) Si considerano Politici Italiani Locali i clienti o i
			titolari effettivi che ricoprono cariche pubbliche in ambiti non ricompresi dalla nozione di
			PEP, ma per i quali comunque sussiste una rilevante esposizione al rischio di corruzione e dei
			reati conto la Pubblica Amministrazione, quali reati presupposto per il riciclaggio e/o il
			finanziamento del terrorismo. Si fa riferimento, ad esempio, agli amministratori locali, a
			soggetti con ruoli apicali nella pubblica amministrazione o in enti pubblici, consorzi e
			associazioni di natura pubblicistica.
		</p>
	);
};

const getEstero = () => {
	return (
		<p style={{ whiteSpace: "pre-line" }}>
			Per US Person si intende: {"\n"}- US Citizen : I cittadini statunitensi, ovunque residenti (la
			nascita negli Stati Uniti comporta l'acquisizione della cittadinanza USA) {"\n"}- US Resident
			: Residenti negli USA oppure soggetti per il quale risulti soddisfatto il Substantial Presence
			Test (aver soggiornato negli USA per almeno 183 gg nell'anno in corso e nei due anni
			precedenti) oppure in possesso di Green Card (permesso di soggiorno permanente negli USA).
			{"\n"}
			La regola non si applica in generale alle persone fisiche temporaneamente presenti negli USA a
			seguito di incarichi di Governi esteri, agli insegnanti ed agli studenti in possesso del
			relativo visto (US VISA).
		</p>
	);
};

const getResidenza = () => {
	return (
		<p style={{ whiteSpace: "pre-line" }}>
			La residenza fiscale può variare da Paese a Paese. {"\n"}Un soggetto, di solito, è fiscalmente
			residente nel Paese o nei Paesi nei quali ha la residenza anagrafica, la dimora abituale o la
			sede principale dei propri interessi economici. {"\n"}Nel Paese di residenza fiscale, si è di
			regola tenuti a presentare dichiarazione e/o a pagare le tasse con riferimento ai redditi
			ovunque percepiti.{"\n"} Per eventuali approfondimenti, ti invitiamo a consultare il sito
			dell'OCSE - Sezione Tax Residency per le normative interne dei singoli Paesi.
		</p>
	);
};

export default CarichePubbliche;
